const hiddenTimelineTypes = {
    sales_day_summary: true,
    sales_tag: true,
    sales_final_status: true,
    offer: true,
    agency_handoff: true,
    sass_paid_client_created_or_linked: true,
    unregistered: true
};

const godfatherTimelineTypes = {
    BRAND_UPDATE: true,
    COMMENT: true,
    OWNER_REGISTERED: true,
    SALES_SESSION: true,
    SASS_SESSION: true,
    OFFER_CREATED: true,
    OFFER_PAID: true,
    OFFER_ACCEPTED: true,
    SASS: true,
    SASS_SET: true,
    SASS_RESCHEDULE: true,
    SASS_IN_PROGRESS: true,
    SASS_LOST: true,
    SASS_PAID: true
};

const placeholderCommentText = {
    "comment added": true
};

// Reads whether a fallback row belongs inside a completed session aggregate.
const read_is_session_scoped_row = (row) => {
    return ["sales_session", "sass_session"].includes(read_timeline_text(row?.timelineScope))
        || read_timeline_text(row?.salesSessionId)
        || read_timeline_text(row?.sassSessionId)
        || read_timeline_text(row?.data?.salesSessionId)
        || read_timeline_text(row?.data?.sassSessionId)
        || row?.hiddenFromTimeline === true;
};

// Removes in-window session memory rows before they become standalone events.
const read_visible_fallback_rows = (rows) => {
    return (rows || []).filter((row) => !read_is_session_scoped_row(row));
};

// Reads one clean text value from current and historical timeline rows.
export const read_timeline_text = (value) => {
    return `${value || ""}`.trim();
};

// Converts stored rich text into safe readable text for the audit trail.
export const read_timeline_plain_text = (value) => {
    const richTextDocument = new DOMParser().parseFromString(read_timeline_text(value), "text/html");
    const decodedTextDocument = new DOMParser().parseFromString(richTextDocument.body.textContent, "text/html");

    return read_timeline_text(decodedTextDocument.body.textContent).replace(/\u00a0/g, " ").replace(/\s+\n/g, "\n").replace(/\n\s+/g, "\n");
};

// Reads one valid date as an exact ISO value for sorting and deduplication.
export const read_timeline_iso_date = (...values) => {
    const dateValue = values.find((value) => {
        const date = new Date(value || 0);

        return !Number.isNaN(date.getTime()) && date.getTime() > 0;
    });
    const date = new Date(dateValue || 0);
    const dateReaders = {
        true: () => "",
        false: () => date.toISOString()
    };

    return dateReaders[Number.isNaN(date.getTime()) || date.getTime() <= 0]();
};

// Normalizes source applications to the supported badge values.
export const read_timeline_source_app = (value) => {
    const sourceAliases = {
        offers: "godfather",
        godfather: "godfather",
        cerebro: "cerebro",
        erised: "erised",
        niffler: "niffler"
    };

    return sourceAliases[read_timeline_text(value).toLowerCase()] || "niffler";
};

// Reads whether one raw row belongs to the Godfather SASS workflow.
const read_is_sass_timeline_row = (row, sourceApp) => {
    const rawType = read_timeline_text(row?.type).toLowerCase().replace(/[\s-]+/g, "_");
    const sourceEntity = read_timeline_text(row?.sourceEntity || row?.entity || row?.workflow).toLowerCase();

    return sourceEntity.includes("sass") || rawType.includes("sass") || (sourceApp === "godfather" && ["set", "meeting", "inprogress", "in_progress", "lost", "paid"].includes(rawType));
};

// Reads whether one SASS set row contains explicit reschedule context.
const read_is_sass_reschedule = (row) => {
    const context = read_timeline_text([
        row?.note,
        row?.message,
        row?.comment,
        row?.data?.note,
        row?.data?.reason,
        row?.to?.note,
        row?.to?.reason
    ].filter(Boolean).join(" ")).toLowerCase();

    return Boolean(row?.rescheduledAt || row?.data?.rescheduledAt || row?.to?.rescheduledAt || context.includes("reschedul"));
};

// Confirms a SASS payment from Active Quest context rather than a manual board move.
const read_is_confirmed_sass_payment = (row) => {
    const rawType = read_timeline_text(row?.type || row?.eventType || row?.action).toLowerCase().replace(/[\s-]+/g, "_");
    const paymentStatus = read_timeline_text(row?.to?.outcome || row?.to?.status || row?.status).toLowerCase().replace(/[\s-]+/g, "_");
    const sourceKey = read_timeline_text(row?.sourceKey || row?.sourceId).toLowerCase();

    return rawType === "sass_actual_paid"
        || paymentStatus === "actually_paid"
        || sourceKey.includes("sass-actual-paid")
        || Boolean(row?.to?.questId || row?.data?.questId || row?.activeQuest?.questId || row?.data?.activeQuest?.questId);
};

// Reads one requested Sales status from current and legacy labels.
const read_timeline_status_type = (value) => {
    const statusTypes = {
        to_call: "TO_CALL",
        new_call: "STALLING",
        meeting_set: "STALLING",
        in_progress: "IN_PROGRESS",
        inprogress: "IN_PROGRESS",
        lost: "LOST",
        customer_lost: "LOST",
        stalling: "STALLING"
    };
    const statusKey = read_timeline_text(value).toLowerCase().replace(/[\s-]+/g, "_");

    return statusTypes[statusKey] || "";
};

// Maps every historical alias into the canonical renderer contract.
export const read_timeline_type = (row) => {
    const sourceApp = read_timeline_source_app(row?.app || row?.sourceApp || row?.source);
    const rawType = read_timeline_text(row?.type || row?.eventType || row?.action).toLowerCase().replace(/[\s-]+/g, "_");
    const note = read_timeline_text(row?.note || row?.text || row?.message || row?.comment || row?.action);
    const statusType = read_timeline_status_type(row?.to?.status || row?.status || note);
    const isSassRow = read_is_sass_timeline_row(row, sourceApp);
    const registrationState = [row?.to?.key, row?.to?.name, row?.data?.tagKey, row?.detail?.tagKey, note, rawType]
        .map((value) => read_timeline_text(value).toLowerCase().replace(/[\s-]+/g, "_"))
        .find((value) => ["registered", "unregistered"].includes(value)) || "";
    const registrationRow = ["tag", "auto", "metadata", "legacy_tag", "registered", "unregistered"].includes(rawType);
    const exactTypes = {
        comment: "COMMENT",
        sass_comment: "COMMENT",
        to_call: "TO_CALL",
        calendar: "STALLING",
        stalling: "STALLING",
        seller: "SELLER_ASSIGNED",
        seller_assigned: "SELLER_ASSIGNED",
        legacy_seller: "SELLER_ASSIGNED",
        seller_removed: "SELLER_REMOVED",
        brand_created: "BRAND_CREATED",
        brand_update: "BRAND_UPDATE",
        brand_archived: "BRAND_ARCHIVED",
        registered: "OWNER_REGISTERED",
        owner_registered: "OWNER_REGISTERED",
        linked_client_update: "BRAND_UPDATE",
        linked_brand_update: "BRAND_UPDATE",
        sass_account_preselected: "BRAND_UPDATE",
        business_linked: "BRAND_UPDATE",
        sales_session: "SALES_SESSION",
        sass_session: "SASS_SESSION",
        client_created: "CLIENT_CREATED",
        client_link: "CLIENT_LINKED",
        client_linked: "CLIENT_LINKED",
        client_link_cleared: "CLIENT_UNLINKED",
        client_unlinked: "CLIENT_UNLINKED",
        agency: "OFFER_CREATED",
        agency_paid: "OFFER_PAID",
        agency_paid_offer: "OFFER_PAID",
        offer_created: "OFFER_CREATED",
        offer_paid: "OFFER_PAID",
        offer_accepted: "OFFER_ACCEPTED",
        sass: "SASS",
        sass_set: "SASS_SET",
        sass_reschedule: "SASS_RESCHEDULE",
        sass_in_progress: "SASS_IN_PROGRESS",
        meeting: "SASS_IN_PROGRESS",
        sass_lost: "SASS_LOST",
        communication: "COMMUNICATION",
        work_completed: "WORK_COMPLETED",
        lead_merged: "PROSPECT_MERGED",
        lead_merge: "PROSPECT_MERGED",
        prospect_merged: "PROSPECT_MERGED",
        legacy_merge: "PROSPECT_MERGED",
        brand_merged: "BRAND_MERGED",
        legacy_event: "LEGACY_EVENT",
        legacy_activity: "LEGACY_ACTIVITY",
        tag: "TAG",
        auto: "TAG",
        metadata: "TAG",
        legacy_tag: "TAG",
        linked_client_sync_warning: "SYNC_WARNING",
        sync_warning: "SYNC_WARNING",
        linked_client_sync_recovered: "SYNC_RECOVERED",
        sync_recovered: "SYNC_RECOVERED",
        generic: "GENERIC"
    };
    const stateReaders = {
        canonical: () => read_timeline_text(row?.type).toUpperCase(),
        hidden: () => "",
        ownerRegistered: () => "OWNER_REGISTERED",
        sassSet: () => ({
            true: "SASS_RESCHEDULE",
            false: "SASS_SET"
        })[read_is_sass_reschedule(row)],
        sassInProgress: () => "SASS_IN_PROGRESS",
        sassLost: () => "SASS_LOST",
        sassPaid: () => ({
            true: "SASS_PAID",
            false: ""
        })[read_is_confirmed_sass_payment(row)],
        status: () => statusType,
        legacyStatus: () => statusType,
        exact: () => exactTypes[rawType] || statusType || "GENERIC"
    };
    const state = [
        [registrationRow && registrationState === "unregistered", "hidden"],
        [registrationRow && registrationState === "registered", "ownerRegistered"],
        [row?.contract === 3, "canonical"],
        [Boolean(hiddenTimelineTypes[rawType]), "hidden"],
        [rawType === "set" && isSassRow, "sassSet"],
        [["in_progress", "inprogress"].includes(rawType) && isSassRow, "sassInProgress"],
        [rawType === "lost" && isSassRow, "sassLost"],
        [["paid", "sass_paid", "sass_actual_paid"].includes(rawType) && isSassRow, "sassPaid"],
        [rawType === "status", "status"],
        [["tag", "auto", "metadata", "legacy_tag", "legacy_status"].includes(rawType) && Boolean(statusType), "legacyStatus"]
    ].find((stateItem) => stateItem[0])?.[1] || "exact";

    return stateReaders[state]();
};

// Reads the actor shape displayed consistently by every timeline item.
const read_timeline_actor = (row) => {
    const actor = row?.by || row?.author || row?.owner || {};
    const actorText = {
        true: actor,
        false: ""
    }[typeof actor === "string"];

    return {
        userId: read_timeline_text(actor?.userId || actor?.id || actor?._id),
        name: read_timeline_text(actor?.name || actor?.fullName || actor?.displayName || actor?.firstname || actor?.firstName || actor?.username || actor?.email || actorText || "System"),
        email: read_timeline_text(actor?.email || actor?.username || actorText),
        username: read_timeline_text(actor?.username || actor?.email || actorText),
        aliases: actor?.aliases || [],
        avatarUrl: read_timeline_text(actor?.avatarUrl || actor?.picture || actor?.photoUrl)
    };
};

// Expands one compact actor into the canonical grouping and display contract.
const read_compact_timeline_actor = (actor) => {
    const aliases = actor?.aliases || [];
    const email = aliases.find((alias) => read_timeline_text(alias).includes("@")) || (read_timeline_text(actor?.id).includes("@") ? actor?.id : "");
    const username = aliases.find((alias) => !read_timeline_text(alias).includes("@")) || actor?.id;

    return {
        userId: read_timeline_text(actor?.id),
        name: read_timeline_text(actor?.label || actor?.id || "System"),
        email: read_timeline_text(email),
        username: read_timeline_text(username),
        aliases
    };
};

// Expands compact change tuples into the canonical renderer representation.
const read_compact_timeline_changes = (changes) => {
    return (changes || []).map((change) => ({
        field: read_timeline_text(change?.[0]),
        from: change?.[1],
        to: change?.[2]
    })).filter((change) => change.field);
};

// Expands compact nested comments while retaining actor and action time.
const read_compact_timeline_comments = (comments) => {
    return (comments || []).map((comment) => ({
        id: read_timeline_text(comment?.id),
        text: read_timeline_text(comment?.text),
        by: read_compact_timeline_actor(comment?.actor),
        createdAt: read_timeline_iso_date(comment?.at)
    }));
};

// Converts one v3 event into the same canonical shape used by legacy fallback rows.
const read_compact_timeline_row = (row, options = {}) => {
    const changes = read_compact_timeline_changes(row?.changes);
    const from = changes.reduce((values, change) => ({ ...values, [change.field]: change.from }), {});
    const to = changes.reduce((values, change) => ({ ...values, [change.field]: change.to }), {});
    const detail = row?.detail || {};
    const issue = ["COMMUNICATION", "WORK_COMPLETED"].includes(row?.type) ? {
        issueCode: detail.code,
        type: detail.kind,
        title: detail.title,
        description: detail.description,
        comments: read_compact_timeline_comments(detail.comments)
    } : null;

    return {
        contract: 3,
        eventId: row?.id,
        sourceKey: row?.id,
        sourceApp: row?.app,
        sourceEntity: "ledger",
        type: row?.type,
        correlationId: row?.group,
        changedAt: row?.type === "LEGACY_ACTIVITY" || options.preserveOccurrenceDate
            ? read_timeline_iso_date(row?.at)
            : read_timeline_iso_date(row?.at, row?.seq),
        acceptedAt: row?.seq,
        by: row?.type === "LEGACY_ACTIVITY"
            ? { ...read_compact_timeline_actor(row?.actor), name: read_timeline_text(row?.actor?.label || row?.actor?.id || "Unknown author") }
            : read_compact_timeline_actor(row?.actor),
        from: changes.length ? from : null,
        to: changes.length ? to : null,
        note: row?.text,
        data: {
            ...detail,
            changes,
            schedule: detail.schedule || "",
            rescheduledAt: row?.type === "SASS_RESCHEDULE" ? detail.schedule || "" : "",
            issue,
            lateDelivery: row?.late === true
        },
        duplicateSourceIds: []
    };
};

const timelineRowReaderLookup = {
    true: read_compact_timeline_row,
    false: (row) => row
};

// Selects compact v3 expansion only when the event carries the v3 identity fields.
const read_timeline_input_row = (row, options) => {
    const compactRow = Boolean(row?.id && row?.app && Object.prototype.hasOwnProperty.call(row, "at"));

    return timelineRowReaderLookup[compactRow](row, options);
};

// Converts one backend or local fallback row into the shared event contract.
export const read_timeline_event = (row, index = 0, options = {}) => {
    const timelineRow = read_timeline_input_row(row, options);
    const sourceApp = read_timeline_source_app(timelineRow?.sourceApp || timelineRow?.source);
    const sourceEntity = read_timeline_text(timelineRow?.sourceEntity || "brand");
    const changedAt = read_timeline_iso_date(timelineRow?.changedAt, timelineRow?.timestamp, timelineRow?.eventAt, timelineRow?.statusMovedAt, timelineRow?.doneAt, timelineRow?.created, timelineRow?.createdAt, timelineRow?.updatedAt);
    const type = read_timeline_type(timelineRow);
    const sourceKey = read_timeline_text(timelineRow?.sourceKey || timelineRow?.sourceId || timelineRow?._id || timelineRow?.id || `${sourceApp}-${sourceEntity}-${index}`);

    return {
        eventId: read_timeline_text(timelineRow?.eventId || timelineRow?._id || timelineRow?.id || sourceKey),
        sourceKey,
        type,
        sourceApp,
        sourceEntity,
        correlationId: read_timeline_text(timelineRow?.correlationId),
        changedAt,
        acceptedAt: read_timeline_iso_date(timelineRow?.acceptedAt),
        by: read_timeline_actor(timelineRow),
        from: timelineRow?.from || null,
        to: timelineRow?.to || null,
        note: read_timeline_text(timelineRow?.note || timelineRow?.message || timelineRow?.comment || timelineRow?.description || timelineRow?.title || timelineRow?.action || timelineRow?.type),
        data: {
            ...(timelineRow?.data || {}),
            changes: timelineRow?.changes || timelineRow?.data?.changes || timelineRow?.to?.changes || [],
            schedule: timelineRow?.schedule || timelineRow?.data?.schedule || timelineRow?.to?.schedule || timelineRow?.to?.meetingDate || "",
            rescheduledAt: timelineRow?.rescheduledAt || timelineRow?.data?.rescheduledAt || timelineRow?.to?.rescheduledAt || "",
            issue: timelineRow?.issue || timelineRow?.data?.issue || null
        },
        timelineScope: read_timeline_text(timelineRow?.timelineScope),
        hiddenFromTimeline: timelineRow?.hiddenFromTimeline === true,
        duplicateSourceIds: timelineRow?.duplicateSourceIds || []
    };
};

// Reads a UTC calendar day without inventing a fallback for an unknown date.
const read_timeline_day_key = (...values) => {
    const value = values.map(read_timeline_text).find(Boolean);
    const date = new Date(value || 0);

    return !Number.isNaN(date.getTime()) && date.getTime() > 0
        ? date.toISOString().slice(0, 10)
        : "";
};

// Detects the SASS label shared by compact legacy actions and session details.
const read_timeline_sass_marker = (...values) => values
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map(read_timeline_text)
    .some((value) => value.toLowerCase().includes("sass"));

// Reads all searchable values from a compact legacy activity child.
const read_legacy_activity_sass_marker = (event = {}) => {
    const rawType = read_timeline_text(event?.type).toLowerCase().replace(/[\s-]+/g, "_");
    const statusLikeType = !rawType || ["action", "status", "sass", "meeting", "sass_status", "sass_set", "sass_in_progress"].includes(rawType) || rawType.startsWith("sass_");
    const explicitLabels = [
        event?.text,
        event?.note,
        event?.message,
        event?.action,
        event?.detail?.label,
        event?.detail?.outcome
    ];

    return statusLikeType && explicitLabels.some((value) => {
        const label = read_timeline_text(value).toLowerCase().replace(/[\s_-]+/g, " ").replace(/[.:]/g, "").trim();

        return label === "sass" || label === "sass meeting" || label.endsWith(" sass");
    });
};

// Reads all searchable values from a completed Sales/Meeting session aggregate.
const read_session_sass_marker = (event = {}) => {
    const sessionEvents = event?.data?.events || [];
    const sessionPeriods = event?.data?.periods || [];

    return read_timeline_sass_marker(
        event?.note,
        event?.data?.outcome,
        event?.data?.status,
        event?.to?.outcome,
        event?.to?.status,
        sessionEvents.flatMap((sessionEvent) => [
            sessionEvent?.type,
            sessionEvent?.text,
            sessionEvent?.note,
            sessionEvent?.detail?.label,
            sessionEvent?.detail?.outcome
        ]),
        sessionPeriods.map((period) => Array.isArray(period) ? period[0] : period?.status)
    );
};

// Returns every known day for a structured session so legacy rows can be matched safely.
const read_session_day_keys = (event = {}) => [...new Set([
    event?.changedAt,
    event?.data?.openedAt,
    event?.data?.closedAt,
    ...(event?.data?.events || []).map((sessionEvent) => sessionEvent?.at || sessionEvent?.changedAt),
    ...(event?.data?.periods || []).flatMap((period) => Array.isArray(period) ? period.slice(1) : [period?.startedAt, period?.endedAt])
].map((value) => read_timeline_day_key(value)).filter(Boolean))];

// Removes only compact SASS actions already represented by a richer same-day session.
const read_without_redundant_legacy_sass_activity = (events = []) => {
    const sessions = events.filter((event) => ["SALES_SESSION", "SASS_SESSION"].includes(event?.type) && read_session_sass_marker(event));

    if (!sessions.length) {
        return events;
    }

    return events.map((event) => {
        if (event?.type !== "LEGACY_ACTIVITY" || !Array.isArray(event?.data?.events)) {
            return event;
        }

        const legacyDay = read_timeline_day_key(event?.data?.day, event?.changedAt);
        const remainingEvents = event.data.events.filter((legacyEvent) => {
            const duplicate = read_legacy_activity_sass_marker(legacyEvent)
                && Boolean(legacyDay)
                && sessions.some((session) => read_session_day_keys(session).includes(legacyDay));

            return !duplicate;
        });

        if (remainingEvents.length === event.data.events.length) {
            return event;
        }

        if (!remainingEvents.length) {
            return null;
        }

        return {
            ...event,
            data: {
                ...event.data,
                events: remainingEvents
            }
        };
    }).filter(Boolean);
};

// Reads normalized text used only for exact duplicate comparison.
const read_normalized_timeline_context = (value) => {
    return read_timeline_plain_text(value).toLowerCase().replace(/\s+/g, " ").trim();
};

// Recursively normalizes object keys and rich text before duplicate comparison.
const read_stable_timeline_value = (value) => {
    const valueType = [
        [Array.isArray(value), "array"],
        [Boolean(value && typeof value === "object"), "object"],
        [typeof value === "string", "string"]
    ].find((typeItem) => typeItem[0])?.[1] || "scalar";
    const valueReaders = {
        array: () => value.map(read_stable_timeline_value),
        object: () => Object.keys(value).sort().reduce((result, key) => {
            result[key] = read_stable_timeline_value(value[key]);

            return result;
        }, {}),
        string: () => read_normalized_timeline_context(value),
        scalar: () => value
    };

    return valueReaders[valueType]();
};

// Serializes normalized event detail into deterministic duplicate context.
const read_stable_timeline_json = (value) => {
    return JSON.stringify(read_stable_timeline_value(value));
};

// Reads one exact duplicate key from canonical type, ISO time, and context.
export const read_timeline_duplicate_key = (event) => {
    const structuredContext = read_stable_timeline_json({
        from: event?.from,
        to: event?.to,
        changes: event?.data?.changes,
        schedule: event?.data?.schedule,
        rescheduledAt: event?.data?.rescheduledAt,
        issueId: event?.data?.issue?.id || event?.data?.issue?._id,
        sessionId: event?.data?.sessionId,
        sessionEvents: ["SALES_SESSION", "SASS_SESSION"].includes(event?.type) ? event?.data?.events : []
    });
    const contextByType = {
        COMMENT: read_normalized_timeline_context(event?.note),
        TAG: read_normalized_timeline_context(event?.note),
        SYNC_WARNING: read_normalized_timeline_context(event?.note),
        SYNC_RECOVERED: read_normalized_timeline_context(event?.note),
        GENERIC: `${read_normalized_timeline_context(event?.note)}|${structuredContext}`
    };
    const context = contextByType[event?.type] || structuredContext;
    const missingDateKey = `source:${event?.sourceKey}`;
    const exactDateKey = [event?.type, event?.changedAt, context].join("|");
    const dateKeys = {
        true: exactDateKey,
        false: missingDateKey
    };

    return dateKeys[Boolean(event?.changedAt)];
};

// Sorts canonical events newest-first without unstable equal-time movement.
export const read_sorted_timeline_events = (events) => {
    return [...(events || [])].sort((firstEvent, secondEvent) => {
        const firstTime = new Date(firstEvent?.changedAt || 0).getTime();
        const secondTime = new Date(secondEvent?.changedAt || 0).getTime();
        const firstAcceptedTime = new Date(firstEvent?.acceptedAt || 0).getTime();
        const secondAcceptedTime = new Date(secondEvent?.acceptedAt || 0).getTime();

        return secondTime - firstTime
            || secondAcceptedTime - firstAcceptedTime
            || read_timeline_text(secondEvent?.eventId || secondEvent?.sourceKey).localeCompare(read_timeline_text(firstEvent?.eventId || firstEvent?.sourceKey));
    });
};

// Selects the richer duplicate and retains every hidden source identity.
const read_merged_timeline_event = (firstEvent, secondEvent) => {
    const firstRichness = read_timeline_text(firstEvent?.note).length + JSON.stringify(firstEvent?.data || {}).length + JSON.stringify(firstEvent?.to || {}).length;
    const secondRichness = read_timeline_text(secondEvent?.note).length + JSON.stringify(secondEvent?.data || {}).length + JSON.stringify(secondEvent?.to || {}).length;
    const winner = secondRichness > firstRichness ? secondEvent : firstEvent;
    const duplicateSourceIds = [
        firstEvent?.sourceKey,
        secondEvent?.sourceKey,
        ...(firstEvent?.duplicateSourceIds || []),
        ...(secondEvent?.duplicateSourceIds || [])
    ].filter(Boolean).filter((sourceId, index, sourceIds) => {
        return sourceId !== winner?.sourceKey && sourceIds.indexOf(sourceId) === index;
    });

    return { ...winner, duplicateSourceIds };
};

// Suppresses exact duplicates as a final UI guard across loaded API pages.
export const read_deduped_timeline_events = (events) => {
    const visibleEvents = [];
    const indexesBySourceKey = new Map();
    const indexesByDuplicateKey = new Map();

    read_sorted_timeline_events(events).forEach((event) => {
        const duplicateKey = read_timeline_duplicate_key(event);
        const existingIndex = indexesBySourceKey.get(event?.sourceKey) ?? indexesByDuplicateKey.get(duplicateKey);

        if (existingIndex !== undefined) {
            visibleEvents[existingIndex] = read_merged_timeline_event(visibleEvents[existingIndex], event);
            return;
        }

        const eventIndex = visibleEvents.length;

        visibleEvents.push(event);
        indexesBySourceKey.set(event?.sourceKey, eventIndex);
        indexesByDuplicateKey.set(duplicateKey, eventIndex);
    });

    return read_sorted_timeline_events(visibleEvents);
};

// Reads the normalized comment or note key used across every loaded page.
export const read_timeline_text_key = (value) => {
    return read_normalized_timeline_context(value);
};

// Reads whether a comment event only carries the generic placeholder label.
const read_is_placeholder_comment_event = (event) => {
    return event?.type === "COMMENT" && Boolean(placeholderCommentText[read_timeline_text_key(event?.note)]);
};

// Removes repeated comments nested inside Communication issue details.
const read_timeline_issue_without_duplicate_comments = (issue, seenTextKeys) => {
    const duplicateSourceIds = [];
    const comments = (issue?.comments || []).filter((comment) => {
        const textKey = read_timeline_text_key(comment?.comment || comment?.text || comment?.body || comment?.note);
        const visible = Boolean(textKey) && !seenTextKeys.has(textKey);

        if (!visible) {
            duplicateSourceIds.push(read_timeline_text(comment?.sourceKey || comment?._id || comment?.id));
        }

        if (textKey) {
            seenTextKeys.add(textKey);
        }

        return visible;
    });

    return {
        issue: { ...issue, comments },
        duplicateSourceIds: duplicateSourceIds.filter(Boolean)
    };
};

// Suppresses globally repeated comment text without removing structural events.
export const read_text_deduped_timeline_events = (events) => {
    const sortedEvents = read_sorted_timeline_events(events);
    const commentsByTextKey = new Map();

    sortedEvents.filter((event) => event?.type === "COMMENT").forEach((event) => {
        const textKey = read_timeline_text_key(event?.note);
        const savedEvent = commentsByTextKey.get(textKey);

        if (textKey) {
            commentsByTextKey.set(textKey, savedEvent ? read_merged_timeline_event(savedEvent, event) : event);
        }
    });

    const seenCommentTextKeys = new Set();
    const seenTextKeys = new Set(commentsByTextKey.keys());
    const visibleEvents = [];

    sortedEvents.forEach((event) => {
        const textKey = read_timeline_text_key(event?.note);

        if (event?.type === "COMMENT" && textKey && !seenCommentTextKeys.has(textKey)) {
            seenCommentTextKeys.add(textKey);
            visibleEvents.push(commentsByTextKey.get(textKey));
            return;
        }

        if (event?.type === "COMMENT" && textKey) {
            return;
        }

        const issue = event?.data?.issue;
        const issueResult = issue
            ? read_timeline_issue_without_duplicate_comments(issue, seenTextKeys)
            : { issue, duplicateSourceIds: [] };
        const nextEvent = {
            ...event,
            note: textKey && seenTextKeys.has(textKey) ? "" : event?.note,
            data: {
                ...(event?.data || {}),
                issue: issueResult.issue
            },
            duplicateSourceIds: [
                ...(event?.duplicateSourceIds || []),
                ...issueResult.duplicateSourceIds
            ].filter((sourceId, index, sourceIds) => sourceId && sourceIds.indexOf(sourceId) === index)
        };

        if (textKey && !seenTextKeys.has(textKey)) {
            seenTextKeys.add(textKey);
        }

        visibleEvents.push(nextEvent);
    });

    return read_sorted_timeline_events(visibleEvents);
};

// Normalizes API rows and excludes hidden or disallowed source events.
const read_visible_timeline_events = (rows, options) => {
    return (rows || []).map((row, index) => read_timeline_event(row, index, options)).filter((event) => {
        const sourceAllowed = event.sourceApp !== "godfather" || Boolean(godfatherTimelineTypes[event.type]);
        const godfatherCommentAllowed = event.sourceApp !== "godfather" || event.type !== "COMMENT" || event.sourceEntity.toLowerCase().includes("sass") || event.sourceEntity === "ledger";
        const commentVisible = !read_is_placeholder_comment_event(event);
        const sessionMemoryVisible = ["SALES_SESSION", "SASS_SESSION"].includes(event.type)
            || (!event.hiddenFromTimeline && !["sales_session", "sass_session"].includes(event.timelineScope) && !event.data?.salesSessionId && !event.data?.sassSessionId);
        const completedSessionVisible = !["SALES_SESSION", "SASS_SESSION"].includes(event.type)
            || Boolean(event.data?.closedAt && (event.data?.outcome || event.to?.outcome));
        const legacyAgencyOutcomeVisible = event.type !== "LEGACY_EVENT"
            || read_normalized_timeline_context(event.note) !== "agency";

        return Boolean(event.type) && sourceAllowed && godfatherCommentAllowed && commentVisible && sessionMemoryVisible && completedSessionVisible && legacyAgencyOutcomeVisible;
    });
};

// Preserves the existing legacy/default presentation contract for its consumers.
export const read_timeline_events = (rows) => {
    const canonicalEvents = read_deduped_timeline_events(read_visible_timeline_events(rows));

    return read_text_deduped_timeline_events(read_without_redundant_legacy_sass_activity(canonicalEvents));
};

// The activity API owns duplicate decisions; pagination only merges matching identities.
export const read_activity_timeline_events = (rows) => {
    const eventsByIdentity = new Map();

    read_visible_timeline_events(rows, { preserveOccurrenceDate: true }).forEach((event) => {
        const identity = `${event.sourceApp}:${event.eventId || event.sourceKey}`;
        const actorName = event.by?.name === "Unknown user" ? "Unknown author" : event.by?.name;

        if (!eventsByIdentity.has(identity)) {
            eventsByIdentity.set(identity, { ...event, by: { ...event.by, name: actorName } });
        }
    });

    return read_sorted_timeline_events(read_without_redundant_legacy_sass_activity([...eventsByIdentity.values()]));
};

// Assembles a local Brand-row fallback before the canonical API responds.
export const read_brand_timeline_fallback = (brandRow) => {
    const timeline = brandRow?.timeline || brandRow?.brandRecord?.timeline || [];
    const fallbackRows = [
        ...read_visible_fallback_rows(brandRow?.comments || brandRow?.brandRecord?.comments || []).map((comment) => ({
            ...comment,
            type: "comment",
            note: comment?.comment,
            by: comment?.author,
            sourceEntity: "brand-comment"
        })),
        ...read_visible_fallback_rows(brandRow?.histories || brandRow?.brandRecord?.history || []).map((history) => ({
            ...history,
            sourceEntity: "brand-history"
        })),
        ...read_visible_fallback_rows(brandRow?.legacy_timeline || brandRow?.brandRecord?.legacy_timeline || []).map((event) => ({
            ...event,
            changedAt: event?.timestamp,
            note: event?.message,
            by: event?.owner,
            sourceEntity: "legacy-brand"
        }))
    ];
    return read_timeline_events([...timeline, ...fallbackRows]);
};
